import { defaultApi } from "@/api";
import { pathToNotification } from "@/global";
import { NewNotifications } from "@/global/interfaces";
import { ref } from "vue";
import { useUserStore } from "@/store";
import { authService } from "@/services";
import { createPinia, setActivePinia } from "pinia";
import { logInfo, logError } from "@/loggers";
import { FEATURE_NAMES } from "@/loggers/types";

const pinia = createPinia();
setActivePinia(pinia);

export const sseState = {
  eventSource: null as EventSource | null
};

const userStore = useUserStore();

export const useSSE = () => {
  const isConnectionOpen = ref(false);

  let isConnecting = false;

  const connectToSSE = async (callback: (data?: NewNotifications) => void): Promise<void> => {
    if (sseState.eventSource || isConnecting) return;

    isConnecting = true;

    const token = userStore.user?.access_token || (await authService.getAccessToken());
    if (!token) {
      logError(FEATURE_NAMES.MVS_ASYNC_DOWNLOAD, "Failed to establish SSE connection after retries.");
      isConnecting = false;
      return;
    }

    sseState.eventSource = await defaultApi.apiUnversioned.sseEvents.connect(pathToNotification, token);

    isConnecting = false;

    if (!sseState.eventSource) {
      logError(FEATURE_NAMES.MVS_ASYNC_DOWNLOAD, "Failed to establish SSE connection after retries.");
      return;
    }

    sseState.eventSource.onopen = () => {
      isConnectionOpen.value = true;
      logInfo(FEATURE_NAMES.MVS_ASYNC_DOWNLOAD, "SSE Connection Established!");
    };

    sseState.eventSource.onmessage = (event: MessageEvent) => {
      try {
        const data: NewNotifications = event.data;
        callback(data);
      } catch (e) {
        logError(FEATURE_NAMES.MVS_ASYNC_DOWNLOAD, `Failed to parse SSE data ${e}`);
      }
    };
  };

  const disconnectSSE = () => {
    if (sseState.eventSource) {
      sseState.eventSource.close();
      sseState.eventSource = null;
      isConnectionOpen.value = false;
      logInfo(FEATURE_NAMES.MVS_ASYNC_DOWNLOAD, "SSE Connection Closed!");
    }
  };

  return {
    connectToSSE,
    disconnectSSE,
    isConnectionOpen
  };
};
